import React, { useState, useEffect } from "react";
import { Calendar, Upload, X, Search, Plus, Trash, Loader } from "lucide-react";
import axios from "axios";
import { BASE_URL } from "utils/constant";
import { debounce } from "lodash";
import { useMemo, useCallback } from "react";
import NewVendorModal from "../Materials/Vendor/NewVendor";
const MaterialPurchaseForm = ({ onClose, vendor, onSuccess }) => {
  const [deliveryStatus, setDeliveryStatus] = useState("Upcoming");
  const [paymentStatus, setPaymentStatus] = useState("Part Payment");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [materials, setMaterials] = useState([]);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showNewMaterialModal, setShowNewMaterialModal] = useState(false);
  const [selectedMaterialIndex, setSelectedMaterialIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [paidAmount, setPaidAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState([
    {
      material_id: "",
      quantity: "",
      unit_price: "",
      gst_percentage: "",
      total_price: 0,
    },
    {
      material_id: "",
      quantity: "",
      unit_price: "",
      gst_percentage: "",
      total_price: 0,
    },
  ]);

  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showNewVendorModal, setShowNewVendorModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(vendor ? vendor : null);
  const [vendors, setVendors] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [customFields, setCustomFields] = useState([]);
  const [errors, setErrors] = useState({});
  const [materialTransactionCatagory, setMaterialTransactionCatagory] =
    useState(null);
  const [gstPercentage, setGstPercentage] = useState(18); // Default 18%
  const [showGstOptions, setShowGstOptions] = useState(false);

  // ===== Derived totals for display =====
  // Purchase amount before GST
  const purchaseAmount = useMemo(() => {
    return selectedMaterials.reduce((sum, m) => {
      const qty = Number(m.quantity) || 0;
      const price = Number(m.unit_price) || 0;
      return sum + qty * price;
    }, 0);
  }, [selectedMaterials]);

  // GST calculated on the purchase amount (per-material GST%)
  const gstAmount = useMemo(() => {
    return selectedMaterials.reduce((sum, m) => {
      const qty = Number(m.quantity) || 0;
      const price = Number(m.unit_price) || 0;
      const gstPerc = Number(m.gst_percentage) || 0;
      return sum + (qty * price * gstPerc) / 100;
    }, 0);
  }, [selectedMaterials]);

  const materialsGrandTotal = purchaseAmount + gstAmount;

  const customFieldsTotal = useMemo(() => {
    return customFields.reduce(
      (sum, field) => sum + Number(field.value || 0),
      0
    );
  }, [customFields]);

  const overallGrandTotal = materialsGrandTotal + customFieldsTotal;

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate delivery date
    if (!deliveryDate) {
      newErrors.deliveryDate = "Delivery date is required";
    }

    // Validate vendor
    if (!selectedVendor) {
      newErrors.vendor = "Vendor selection is required";
    }

    // Validate at least one material with all required fields
    const validMaterials = selectedMaterials.filter(
      (m) => m.material_id && m.quantity && m.unit_price
    );

    if (validMaterials.length === 0) {
      newErrors.materials = "At least one material with all fields is required";
    }

    // Validate paid amount for part payment
    if (paymentStatus === "Part Payment" && (!paidAmount || paidAmount <= 0)) {
      newErrors.paidAmount = "Paid amount is required for part payment";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchVendors = async () => {
    try {
      const siteId = localStorage.getItem("site");
      const response = await axios.post(
        `${BASE_URL}/inventory/list-site-vendors/`,
        {
          sites: siteId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        }
      );
      setVendors(response.data.data);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  // Vendor selection handler
  const handleVendorSelect = (vendor) => {
    setSelectedVendor(vendor);
    setShowVendorModal(false);
    if (errors.vendor) {
      setErrors({ ...errors, vendor: undefined });
    }
  };

  const fetchMaterials = async () => {
    try {
      const siteId = localStorage.getItem("site");
      const token = localStorage.getItem("access");

      const response = await axios.post(
        `${BASE_URL}/inventory/get-materials-of-a-site/`,
        { sites: siteId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setAvailableMaterials(response.data.data);
    } catch (error) {
      console.error("Error fetching materials:", error);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, []);

  useEffect(() => {
    const fetchTransactionCatagories = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/finance/get-all-finance-catagory/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        if (response.ok) {
          const res = await response.json();
          const data = res.data;
          const purchaseCatagory = data.find(
            (cat) => cat.catagory_name === "Material Transaction"
          );
          if (purchaseCatagory) {
            setMaterialTransactionCatagory(purchaseCatagory.id);
          }
        } else {
          console.error("Failed to fetch material transaction catagories");
        }
      } catch (error) {
        console.error("Error fetching material transaction catagories:", error);
      }
    };
    fetchTransactionCatagories();
  }, []);
  // Modify handleSave to include file upload
  const handleSave = async () => {
    if (!validateForm()) {
      // Form has errors, don't submit
      return;
    }
    setIsLoading(true);
    try {
      const token = localStorage.getItem("access");
      const siteId = localStorage.getItem("site");

      const validMaterials = selectedMaterials.filter(
        (m) => m.material_id && m.quantity && m.unit_price
      );
      const validCustomFields = customFields.filter(
        (field) => field.name && field.value
      );
      const totalPrice =
        validMaterials.reduce((sum, m) => sum + m.total_price, 0) +
        customFields.reduce((sum, field) => sum + Number(field.value), 0);
      const remainingPayment = totalPrice - Number(paidAmount);

      // Create FormData
      const formData = new FormData();

      // Add receipt images if any
      selectedFiles.forEach((file, index) => {
        formData.append("receipt_image", file);
      });

      // Add other data
      const payload = {
        date: deliveryDate,
        total_price: totalPrice,
        payment_method: paymentMethod,
        payment_status: paymentStatus.toLowerCase().replace(" ", "-"),
        paid_amount: Number(paidAmount),
        remaining_payment: remainingPayment,
        remarks,
        transaction_catagory_id: materialTransactionCatagory,
        transaction_id: deliveryStatus === "Upcoming" ? 1 : 2,
        sites: Number(siteId),
        vendor: selectedVendor.id,
        all_materials: validMaterials,
        custom_fields: JSON.stringify(validCustomFields),
      };

      // Append each field to formData
      Object.keys(payload).forEach((key) => {
        if (key === "all_materials") {
          formData.append(key, JSON.stringify(payload[key]));
        } else {
          formData.append(key, payload[key]);
        }
      });

      await axios.post(`${BASE_URL}/inventory/create-delivery/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(progress);
        },
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error saving delivery:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMaterialSelect = (materialId, index) => {
    const material = availableMaterials.find((m) => m.id === materialId);
    const updatedMaterials = [...selectedMaterials];
    updatedMaterials[index] = {
      ...updatedMaterials[index],
      material_id: materialId,
      material_name: material.material_name,
      uom: material.uom,
    };
    setSelectedMaterials(updatedMaterials);
    setShowMaterialModal(false);
    if (
      errors.materials &&
      materialId &&
      updatedMaterials[index].quantity &&
      updatedMaterials[index].unit_price
    ) {
      setErrors({ ...errors, materials: undefined });
    }
  };

  // Helper to calculate total inclusive of GST
  const calculateTotalWithGst = (qty, price, gstPerc) => {
    const base = Number(qty) * Number(price);
    const gstAmt = base * (Number(gstPerc || 0) / 100);
    return base + gstAmt;
  };

  const handleQuantityChange = (index, quantity) => {
    const updatedMaterials = [...selectedMaterials];
    const current = updatedMaterials[index];
    const newQty = Number(quantity);
    updatedMaterials[index] = {
      ...current,
      quantity: newQty,
      total_price: calculateTotalWithGst(
        newQty,
        current.unit_price || 0,
        current.gst_percentage || 0
      ),
    };
    setSelectedMaterials(updatedMaterials);
    if (
      errors.materials &&
      updatedMaterials[index].material_id &&
      quantity &&
      updatedMaterials[index].unit_price
    ) {
      setErrors({ ...errors, materials: undefined });
    }
  };

  const handleUnitPriceChange = (index, price) => {
    const updatedMaterials = [...selectedMaterials];
    const current = updatedMaterials[index];
    const newPrice = Number(price);
    updatedMaterials[index] = {
      ...current,
      unit_price: newPrice,
      total_price: calculateTotalWithGst(
        current.quantity || 0,
        newPrice,
        current.gst_percentage || 0
      ),
    };
    setSelectedMaterials(updatedMaterials);

    if (
      errors.materials &&
      updatedMaterials[index].material_id &&
      updatedMaterials[index].quantity &&
      price
    ) {
      setErrors({ ...errors, materials: undefined });
    }
  };

  // Handle GST percentage change per material
  const handleGstChange = (index, gst) => {
    const updatedMaterials = [...selectedMaterials];
    const current = updatedMaterials[index];
    const gstPerc = Number(gst);
    updatedMaterials[index] = {
      ...current,
      gst_percentage: gstPerc,
      total_price: calculateTotalWithGst(
        current.quantity || 0,
        current.unit_price || 0,
        gstPerc
      ),
    };
    setSelectedMaterials(updatedMaterials);
  };

  const MaterialModal = () => {
    const [searchTerm, setSearchTerm] = useState("");

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-md">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Select Material</h2>
              <button onClick={() => setShowMaterialModal(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <button
                onClick={() => {
                  setShowMaterialModal(false);
                  setShowNewMaterialModal(true);
                }}
                className="w-full p-3 bg-purple-100 text-purple-700 rounded-lg flex items-center justify-center"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add New Material
              </button>
            </div>

            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Search Your Material"
                className="w-full py-2 pl-10 border rounded-md"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {availableMaterials
                .filter((m) =>
                  m.material_name
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
                )
                .map((material) => (
                  <button
                    key={material.id}
                    onClick={() =>
                      handleMaterialSelect(material.id, selectedMaterialIndex)
                    }
                    className="w-full p-3 text-left hover:bg-gray-100 rounded-lg"
                  >
                    <div className="font-medium">{material.material_name}</div>
                    <div className="text-sm text-gray-600">
                      {material.specification}
                    </div>
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const NewMaterialModal = () => {
    const [showCustomUom, setShowCustomUom] = useState(false);
    const [newMaterial, setNewMaterial] = useState({
      material_name: "",
      specification: "",
      subcategory: "",
      uom: "",
      current_stock: 0,
    });
    const handleCreateMaterial = async () => {
      try {
        const token = localStorage.getItem("access");
        const siteId = localStorage.getItem("site");

        await axios.post(
          `${BASE_URL}/inventory/create-material/`,
          {
            ...newMaterial,
            sites: siteId,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        fetchMaterials();
        setShowNewMaterialModal(false);
        setNewMaterial({
          material_name: "",
          specification: "",
          subcategory: "",
          uom: "",
          current_stock: 0,
        });
      } catch (error) {
        console.error("Error creating material:", error);
      }
    };
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-md">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">New Material</h2>
              <button onClick={() => setShowNewMaterialModal(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Material Name
                </label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={newMaterial.material_name}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      material_name: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Material Subcategory
                </label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={newMaterial.subcategory}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      subcategory: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Specification
                </label>
                <input
                  type="text"
                  placeholder="Brand, Color, Quality etc."
                  className="w-full p-2 border rounded-md"
                  value={newMaterial.specification}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      specification: e.target.value,
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit of measurement
                </label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={newMaterial.uom}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "custom") {
                      setShowCustomUom(true);
                      setNewMaterial({
                        ...newMaterial,
                        uom: "",
                      });
                    } else {
                      setShowCustomUom(false);
                      setNewMaterial({
                        ...newMaterial,
                        uom: value,
                      });
                    }
                  }}
                >
                  <option value="">Select unit</option>
                  <option value="Bags">Bags</option>
                  <option value="Meters(M)">Meters(M)</option>
                  <option value="Liters(L)">Liters(L)</option>
                  <option value="custom">Custom Unit</option>
                </select>

                {showCustomUom && ( // removed === true since showCustomUom is already a boolean
                  <input
                    type="text"
                    placeholder="Enter custom unit"
                    className="w-full p-2 border rounded-md mt-2"
                    value={newMaterial.uom}
                    onChange={(e) =>
                      setNewMaterial({
                        ...newMaterial,
                        uom: e.target.value,
                      })
                    }
                  />
                )}
              </div>

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Stock
                </label>
                <input
                  type="number"
                  className="w-full p-2 border rounded-md"
                  value={newMaterial.current_stock}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      current_stock: Number(e.target.value),
                    })
                  }
                />
              </div> */}

              <button
                onClick={handleCreateMaterial}
                className="w-full bg-[#3B0069] text-white py-2 rounded-md font-medium hover:bg-purple-700"
              >
                Save & Continue Purchase
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const VendorModal = () => {
    const [vendorSearchTerm, setvendorSearchTerm] = useState("");

    // Memoize the filtered vendors to prevent unnecessary recalculations
    const filteredVendors = useMemo(() => {
      return vendors.filter((vendor) =>
        vendor.name.toLowerCase().includes(vendorSearchTerm.toLowerCase())
      );
    }, [vendors, vendorSearchTerm]);

    // Debounce the search term update
    const handleSearchChange = useCallback(
      debounce((value) => {
        setvendorSearchTerm(value);
      }, 100),
      []
    );

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-md">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Select Vendor/Shop</h2>
              <button onClick={() => setShowVendorModal(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-4">
              <button
                onClick={() => {
                  setShowVendorModal(false);
                  setShowNewVendorModal(true);
                }}
                className="w-full p-3 bg-purple-100 text-purple-700 rounded-lg flex items-center justify-center"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add New Vendor
              </button>
            </div>
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Search Vendor"
                className="w-full py-2 px-8 border rounded-md"
                onChange={(e) => handleSearchChange(e.target.value)}
                // Remove the value prop to make it controlled by the DOM
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              {/* Changed positioning to be vertically centered */}
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {filteredVendors.map((vendor) => (
                <div
                  key={vendor.id}
                  onClick={() => handleVendorSelect(vendor)}
                  className="w-full p-3 text-left hover:bg-gray-100 rounded-lg cursor-pointer flex justify-between items-center"
                >
                  <div>
                    <div className="font-medium">{vendor.name}</div>
                    <div className="text-sm text-gray-600">
                      +91 {vendor.contact_no}
                    </div>
                  </div>
                  <span className="text-sm bg-gray-200 px-2 py-1 rounded-md">
                    {vendor.vendor_type}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };
  const handleDeleteMaterial = (index) => {
    const updatedMaterials = selectedMaterials.filter((_, i) => i !== index);
    setSelectedMaterials(updatedMaterials);
  };
  return (
    <div className="w-full p-6 bg-gray-50">
      <div className="flex justify-between  items-center mb-4">
        <div className="flex items-center">
          <button
            className="mr-3"
            onClick={() => {
              onClose();
            }}
          >
            {/* Back Arrow Icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <h1 className="flex justify-center items-center text-lg font-semibold">
            Material Purchase
          </h1>
        </div>
        <button className="text-gray-600">
          {/* More Options Icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
            />
          </svg>
        </button>
      </div>
      {/* Original form JSX remains the same until the Materials Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Delivery No.</p>
              <p className="font-semibold">#23-09-456</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Delivery Date *
              </label>
              <div className="relative">
                <input
                  type="date"
                  className={`w-full border rounded-md pl-10 py-2 pr-2 ${
                    errors.deliveryDate ? "border-red-500" : ""
                  }`}
                  value={deliveryDate}
                  onChange={(e) => {
                    setDeliveryDate(e.target.value);
                    if (errors.deliveryDate) {
                      setErrors({ ...errors, deliveryDate: undefined });
                    }
                  }}
                />
                <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
              {errors.deliveryDate && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.deliveryDate}
                </p>
              )}
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">
                Delivery Status
              </p>
              <div className="flex space-x-2">
                <button
                  className={`px-3 py-1 rounded-full text-sm ${
                    deliveryStatus === "Upcoming"
                      ? "bg-purple-100 text-purple-700"
                      : "bg-gray-200 text-gray-700"
                  }`}
                  onClick={() => setDeliveryStatus("Upcoming")}
                >
                  Upcoming
                </button>
                <button
                  className={`px-3 py-1 rounded-full text-sm ${
                    deliveryStatus === "Delivered"
                      ? "bg-purple-100 text-purple-700"
                      : "bg-gray-200 text-gray-700"
                  }`}
                  onClick={() => setDeliveryStatus("Delivered")}
                >
                  Delivered
                </button>
              </div>
            </div>
          </div>

          {/* Payment Details */}
          <div className="bg-white p-4 rounded-lg space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">
                Payment Status
              </p>
              <div className="flex flex-wrap gap-2">
                {["Pre-Paid", "Part Payment", "Post-Paid"].map((status) => (
                  <button
                    key={status}
                    className={`px-3 py-1 rounded-full text-sm ${
                      paymentStatus === status
                        ? "bg-purple-100 text-purple-700"
                        : "bg-gray-200 text-gray-700"
                    }`}
                    onClick={() => {
                      setPaymentStatus(status);
                      if (status === "Pre-Paid") {
                        setPaidAmount(overallGrandTotal);
                      } else if (status === "Post-Paid") {
                        setPaidAmount(0);
                      }
                    }}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">
                Payment Method
              </p>
              <div className="flex flex-wrap gap-2">
                {["Cash", "UPI/Bank Transfer", "Cheque"].map((method) => (
                  <button
                    key={method}
                    className={`px-3 py-1 rounded-full text-sm ${
                      paymentMethod === method
                        ? "bg-purple-100 text-purple-700"
                        : "bg-gray-200 text-gray-700"
                    }`}
                    onClick={() => setPaymentMethod(method)}
                  >
                    {method}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paid Amount {paymentStatus === "Part Payment" ? "*" : ""}
              </label>
              <input
                type="number"
                placeholder="Amount"
                className={`w-full p-2 border rounded-md ${
                  errors.paidAmount ? "border-red-500" : ""
                }`}
                value={paidAmount}
                disabled={
                  paymentStatus === "Post-Paid" || paymentStatus === "Pre-Paid"
                }
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const numericValue = Number(inputValue);

                  // Check if part payment and amount exceeds remaining payment
                  if (
                    paymentStatus === "Part Payment" &&
                    numericValue > overallGrandTotal
                  ) {
                    setErrors({
                      ...errors,
                      paidAmount: `Paid amount cannot exceed remaining payment of ₹${overallGrandTotal.toFixed(
                        2
                      )}`,
                    });
                    // Don't update the state - this prevents the user from typing beyond the limit
                    return;
                  }

                  setPaidAmount(inputValue);
                  if (errors.paidAmount) {
                    setErrors({ ...errors, paidAmount: undefined });
                  }
                }}
              />
              {errors.paidAmount && (
                <p className="text-red-500 text-xs mt-1">{errors.paidAmount}</p>
              )}
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">
                Remaining Payment
              </p>
              <p className="font-semibold">
                ₹ {(overallGrandTotal - Number(paidAmount || 0)).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Vendor Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor Details
            </label>
            <button
              onClick={() => setShowVendorModal(true)}
              className="w-full p-2 border rounded-md text-left bg-white"
            >
              {selectedVendor ? selectedVendor.name : "Select Vendor/Shop"}
            </button>
            {errors.vendor && (
              <p className="text-red-500 text-xs mt-1">{errors.vendor}</p>
            )}
          </div>

          {/* Materials Section */}
          <div className="bg-white p-4 rounded-lg space-y-4">
            <div className="flex justify-between items-center">
              <p className="font-medium">Materials</p>

              <button
                className="text-purple-500 font-medium bg-[#3B0069] hover:bg-purple-900 rounded-full flex flex-row p-2 items-center justify-center"
                onClick={() => {
                  setSelectedMaterials([
                    ...selectedMaterials,
                    {
                      material_id: "",
                      quantity: "",
                      unit_price: "",
                      gst_percentage: "",
                      total_price: 0,
                    },
                  ]);
                }}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {errors.materials && (
              <p className="text-red-500 text-xs">{errors.materials}</p>
            )}
            {selectedMaterials.map((material, index) => (
              <div key={index} className="space-y-2">
                <div className="relative flex items-center">
                  <button
                    className={`w-full p-2 border rounded-md text-left bg-white ${
                      errors.materials && !material.material_id
                        ? "border-red-500"
                        : ""
                    }`}
                    onClick={() => {
                      setSelectedMaterialIndex(index);
                      setShowMaterialModal(true);
                    }}
                  >
                    {material.material_name || "Select Material"}
                  </button>
                  <button
                    className="ml-2 text-red-600"
                    onClick={() => handleDeleteMaterial(index)}
                  >
                    <Trash className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex justify-between gap-2 flex-wrap">
                  <input
                    type="text"
                    placeholder="Total Unit"
                    className={`w-1/4 p-2 border rounded-md ${
                      errors.materials && !material.quantity
                        ? "border-red-500"
                        : ""
                    }`}
                    value={material.quantity || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow numbers
                      if (/^\d*$/.test(value)) {
                        handleQuantityChange(index, value);
                      }
                    }}
                    inputMode="numeric" // Shows numeric keyboard on mobile
                    pattern="[0-9]*" // Additional validation
                  />
                  <input
                    type="text"
                    placeholder="Unit"
                    className="w-1/4 p-2 border rounded-md bg-gray-50"
                    value={material.uom || ""}
                    readOnly
                  />
                  <input
                    type="number"
                    placeholder="Unit Price"
                    className={`w-1/4 p-2 border rounded-md ${
                      errors.materials && !material.unit_price
                        ? "border-red-500"
                        : ""
                    }`}
                    value={material.unit_price || ""}
                    onChange={(e) =>
                      handleUnitPriceChange(index, e.target.value)
                    }
                  />
                  {/* GST input with trailing % sign */}
                  <div className="relative w-1/4">
                    <input
                      type="text"
                      placeholder="GST"
                      className="w-full p-2 pr-6 border rounded-md"
                      value={material.gst_percentage || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow numbers and decimal point for GST percentage
                        if (/^\d*\.?\d*$/.test(value)) {
                          handleGstChange(index, value);
                        }
                      }}
                      inputMode="decimal" // Shows decimal keyboard on mobile
                      pattern="[0-9]*\.?[0-9]*" // Additional validation for decimal
                    />
                    <span className="absolute inset-y-0 right-2 flex items-center text-gray-500 text-sm">
                      %
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-sm font-medium">Total Price</p>
                  <p className="font-semibold">
                    ₹ {material.total_price?.toFixed(2) || "0.00"}
                  </p>
                </div>
              </div>
            ))}
            {/* Purchase & GST breakdown */}
            <div className="flex justify-between items-center pt-2 border-t">
              <p className="font-medium">Purchase Amount</p>
              <p className="font-semibold">₹ {purchaseAmount.toFixed(2)}</p>
            </div>
            <div className="flex justify-between items-center">
              <p className="font-medium">GST Amount</p>
              <p className="font-semibold">₹ {gstAmount.toFixed(2)}</p>
            </div>
            <div className="flex justify-between items-center font-semibold pt-2 border-t">
              <p className="font-medium">Grand Total</p>
              <p className="font-semibold">
                ₹ {materialsGrandTotal.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Custom payment fields, which is a name-value pair, can be added and removed */}
          <div className="bg-white p-4 rounded-lg space-y-4">
            {/* <div className="flex justify-between items-center">
              <p className="font-medium">Custom Fields</p>
              <button
                className="text-purple-600 font-medium"
                onClick={() =>
                  setCustomFields([...customFields, { name: "", value: "" }])
                }
              >
                +
              </button>
            </div> */}
            {/* Inside the Custom Fields section, add this after the header */}
            <div className="flex justify-between items-center">
              <p className="font-medium">Custom Fields</p>
              <div className="flex space-x-2">
                <button
                  className="text-purple-500 font-medium bg-[#3B0069] hover:bg-purple-900 rounded-full flex flex-row px-2 items-center justify-center"
                  onClick={() =>
                    setCustomFields([...customFields, { name: "", value: "" }])
                  }
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
            {customFields.map((field, index) => (
              <div key={index} className="flex space-x-2">
                <input
                  type="text"
                  placeholder="Field Name"
                  className="w-1/2 p-2 border rounded-md"
                  value={field.name}
                  onChange={(e) =>
                    setCustomFields(
                      customFields.map((f, i) =>
                        i === index ? { ...f, name: e.target.value } : f
                      )
                    )
                  }
                />
                <input
                  type="text"
                  placeholder="Field Value"
                  className="w-1/2 p-2 border rounded-md"
                  value={field.value}
                  onChange={(e) =>
                    setCustomFields(
                      customFields.map((f, i) =>
                        i === index ? { ...f, value: e.target.value } : f
                      )
                    )
                  }
                />
                <button
                  className="text-red-600"
                  onClick={() =>
                    setCustomFields(customFields.filter((_, i) => i !== index))
                  }
                >
                  <Trash className="h-5 w-5" />
                </button>
              </div>
            ))}

            <div className="flex justify-between items-center pt-2 border-t">
              <p className="font-medium">Total Custom Fields</p>
              <p className="font-semibold">
                ₹{" "}
                {customFields.reduce(
                  (sum, field) => sum + Number(field.value || 0),
                  0
                )}
              </p>
            </div>
            <div className="flex justify-between items-center pt-2 border-t">
              <p className="font-medium">Grand Total</p>
              <p className="font-semibold">
                ₹ {materialsGrandTotal.toFixed(2)} +{" "}
                {customFieldsTotal.toFixed(2)} = {overallGrandTotal.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Remarks and Invoice */}
      <div className="mt-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Remarks/Comments (Optional)
          </label>
          <textarea
            className="w-full p-2 border rounded-md"
            rows="3"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          ></textarea>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">
            Invoice (Optional)
          </p>
          <div className="space-y-3">
            <input
              type="file"
              multiple
              accept="image/*,.pdf"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="flex items-center justify-center p-2 border border-purple-300 rounded-md text-purple-600 cursor-pointer hover:bg-purple-50"
            >
              <Upload className="h-5 w-5 mr-2" />
              Upload Photos
            </label>

            {/* Display selected files */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                  >
                    <div className="flex items-center">
                      <div className="ml-2">
                        <p className="text-sm font-medium truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload progress */}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-purple-600 h-2.5 rounded-full"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-6">
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="w-full bg-[#3B0069] text-white py-2 rounded-md font-medium hover:bg-purple-700 transition duration-300"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <Loader className="h-5 w-5 animate-spin" />
              Processing...
            </div>
          ) : (
            "Save & Continue Purchase"
          )}
        </button>
      </div>

      {/* Modals */}
      {/* Vendor Modal */}
      {showVendorModal && <VendorModal />}
      {showMaterialModal && <MaterialModal />}
      {showNewMaterialModal && <NewMaterialModal />}
      {showNewVendorModal && (
        <NewVendorModal
          onClose={() => {
            setShowNewVendorModal(false);
            fetchVendors();
          }}
          isOpen={showNewVendorModal}
        />
      )}
    </div>
  );
};

export default MaterialPurchaseForm;
